import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { AUTH, BRAND } from "@/config";
import { supabase } from "@/lib/supabase";
import { authRedirectUrl, clearExistingSession, formatUsPhone, friendlyAuthError } from "@/lib/auth";
import { useIsInAppBrowser } from "@/lib/in-app-browser";
import {
  AuthInput,
  AuthLabel,
  AuthShell,
  Divider,
  ErrorMessage,
  GoogleButton,
  InAppBrowserNotice,
  PasswordInput,
  PrimaryButton,
} from "@/components/auth/AuthShell";

export const Route = createFileRoute("/signup")({
  component: SignupPage,
});

function SignupPage() {
  const navigate = useNavigate();
  const inAppBrowser = useIsInAppBrowser();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function validate(): string | null {
    if (!firstName.trim() || !lastName.trim()) return "Please enter your first and last name.";
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return "Please enter a valid email.";
    if (password.length < AUTH.minPasswordLength) return `Password must be at least ${AUTH.minPasswordLength} characters.`;
    if (phone.replace(/\D/g, "").length !== 10) return "Please enter a valid US phone number.";
    return null;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const problem = validate();
    if (problem) return setError(problem);
    setError(null);
    setSubmitting(true);
    try {
      await clearExistingSession();
      const { error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: authRedirectUrl(),
          data: { first_name: firstName.trim(), last_name: lastName.trim(), phone, sms_consent: smsConsent },
        },
      });
      if (signUpError) return setError(friendlyAuthError(signUpError.message));
      navigate({ to: "/verify-email", search: { email: email.trim() } });
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogle() {
    setError(null);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: authRedirectUrl() },
    });
    if (oauthError) setError(friendlyAuthError(oauthError.message));
  }

  return (
    <AuthShell title="Create family account" subtitle="Parents and guardians register here. You'll add your player after signing in.">
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <AuthLabel htmlFor="firstName">First name</AuthLabel>
            <AuthInput id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" required />
          </div>
          <div>
            <AuthLabel htmlFor="lastName">Last name</AuthLabel>
            <AuthInput id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" required />
          </div>
        </div>
        <div>
          <AuthLabel htmlFor="email">Email</AuthLabel>
          <AuthInput id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
        </div>
        <div>
          <AuthLabel htmlFor="password">Password</AuthLabel>
          <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" required />
          <p className="mt-1 text-xs text-white/40">At least {AUTH.minPasswordLength} characters.</p>
        </div>
        <div>
          <AuthLabel htmlFor="phone">Mobile phone</AuthLabel>
          <AuthInput
            id="phone"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(formatUsPhone(e.target.value))}
            placeholder="(555) 555-5555"
            autoComplete="tel"
            required
          />
        </div>
        <label className="flex cursor-pointer items-start gap-2 text-xs leading-snug text-white/70">
          <input type="checkbox" className="mt-0.5 accent-a1-red" checked={smsConsent} onChange={(e) => setSmsConsent(e.target.checked)} />
          <span>
            I agree to receive text messages from {BRAND.name} about schedules, programs and account updates.
            Consent is not a condition of enrollment. Message frequency varies; message &amp; data rates may apply.
            Reply STOP to unsubscribe, HELP for help.
          </span>
        </label>
        <ErrorMessage message={error} />
        <p className="text-xs leading-snug text-white/60">
          By creating an account you agree to our{" "}
          <a href={`${BRAND.websiteUrl}/terms`} target="_blank" rel="noopener noreferrer" className="text-white underline decoration-a1-red underline-offset-2">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href={`${BRAND.websiteUrl}/privacy`} target="_blank" rel="noopener noreferrer" className="text-white underline decoration-a1-red underline-offset-2">
            Privacy Policy
          </a>
          .
        </p>
        <PrimaryButton type="submit" disabled={submitting}>
          {submitting ? "Creating account…" : "Create account"}
        </PrimaryButton>
      </form>

      <p className="mt-5 text-center text-sm text-white/70">
        Already have an account?{" "}
        <Link to="/login" className="font-semibold text-white underline decoration-a1-red underline-offset-4">
          Sign in
        </Link>
      </p>

      {inAppBrowser && <InAppBrowserNotice />}
      {AUTH.googleEnabled && !inAppBrowser && (
        <>
          <Divider />
          <GoogleButton onClick={handleGoogle} label="Sign up with Google" />
        </>
      )}
    </AuthShell>
  );
}
